import { formatTimeRange } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-dates';
import { getSessionStatusDisplay } from '@efdigital/ll-addon-coaching/utils/coaching-sessions/session-display';
import type { CoachingSession } from '@efdigital/ll-addon-data';
import type { CoachCoacheePii } from '@efdigital/ll-cdk-coaching-sessions-api-types';
import ActionRequiredDashboardRow from '@efdigital/ll-xceed-v2/components/coaching/action-required-dashboard-row.gts';
import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';

interface DashboardSessionRowSignature {
  Args: {
    session: CoachingSession;
    coachee?: CoachCoacheePii;
    alert?: boolean;
  };
  Element: HTMLDivElement;
}

function toDate(date: string): Date {
  return new Date(date);
}

function sessionMonth(startTime: string): string {
  return new Date(startTime)
    .toLocaleString(undefined, { month: 'short' })
    .toUpperCase();
}

function sessionDay(startTime: string): string {
  return new Date(startTime).toLocaleString(undefined, { day: 'numeric' });
}

function sessionTypeLabel(sessionType: CoachingSession['sessionType']): string {
  if (sessionType === 'coaching') {
    return 'Coaching session';
  }

  return sessionType.replace(/-/g, ' ');
}

function coacheeName(
  coachee: CoachCoacheePii | undefined,
  session: CoachingSession
): string {
  return coachee?.displayName ?? session.user;
}

/** Compact session row for coach dashboard cards. */
const DashboardSessionRow = <template>
  {{#if @alert}}
    {{#let (getSessionStatusDisplay @session.status) as |statusDisplay|}}
      <ActionRequiredDashboardRow
        @session={{@session}}
        @personLabel={{coacheeName @coachee @session}}
        @statusLabel={{statusDisplay.label}}
        @linkRoute="coaching.coach.sessions.action-required"
        ...attributes
      />
    {{/let}}
  {{else}}
    <div
      class="card border rounded-3 position-relative overflow-hidden"
      ...attributes
    >
      <div class="card-body p-2 p-sm-3">
        <div class="d-flex align-items-center gap-3 min-w-0">
          <div class="flex-shrink-0 text-center lh-1">
            <p class="gud-eyebrow text-primary mb-0">
              {{sessionMonth @session.startTime}}
            </p>
            <p class="fs-2 fw-bold mb-0">
              {{sessionDay @session.startTime}}
            </p>
          </div>

          <div class="flex-grow-1 min-w-0">
            <h3 class="h6 fw-bold mb-1 text-capitalize text-break">
              {{sessionTypeLabel @session.sessionType}}
            </h3>
            <p class="small text-body-secondary mb-0 text-break">
              {{coacheeName @coachee @session}}
              ·
              {{formatTimeRange
                (toDate @session.startTime)
                (toDate @session.endTime)
              }}
            </p>
          </div>

          <div class="flex-shrink-0">
            <iconify-icon
              inline
              icon="ph:caret-right"
              class="fs-4 text-primary"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
      <LinkTo @route="coaching.coach.sessions" class="stretched-link">
        <span class="visually-hidden">View
          {{sessionTypeLabel @session.sessionType}}
          with
          {{coacheeName @coachee @session}}
          on Sessions page</span>
      </LinkTo>
    </div>
  {{/if}}
</template> satisfies TOC<DashboardSessionRowSignature>;

export default DashboardSessionRow;
